// src/types/qbank.ts
// Mirror of QBank `Models.Json.Wallet` (camelCase wire format, no envelope).
// IMPORTANT: the Wallet has NO `model` field (that lives on Coin). An EXP coin
// is identified by a non-null `level` / `toNextLevel`. Hand-kept in sync with
// QBank/Models.cs:99-123 (owned by Gionko).
export interface Wallet {
  currencyId: string;
  sub: string;
  name: string;
  code: string;
  exchange: number | null;
  isDefault: boolean | null;
  amount: number | null;
  defaultCoin: number | null;
  isEnable: boolean | null;
  /** EXP coins only (model=1). null for currency coins (model=0). */
  level: number | null;
  /** EXP coins only — points to the next level. */
  toNextLevel: number | null;
}

/**
 * Display fallback for an entitled user who has no currency wallet yet. QBank
 * creates wallet rows lazily (only on first credit/transfer), so an uncredited
 * user's `GET /wallets/get` returns `[]` — there's no coin to read a code from.
 * We still want a zero balance visible, so the Hub/Profile fall back to this
 * default coin and render "0 QC". Replace if the platform's default coin changes.
 */
export const QBANK_DEFAULT_COIN = { code: 'QC', name: 'QLASH Coin' } as const;

// ─── Leagues (mirror of QBank LeaguesController `GET /leagues/my`) ────────────
// Read scope: `qbank:league:read`. Tolerant/optional fields — kept in sync with
// QBank/Controllers/LeaguesController.cs (owned by Gionko). `/leagues/my`
// returns only ACTIVE leagues, so it's empty until a league starts.

export interface LeagueMomentum {
  rankDelta: number | null;
  scoreDelta: number | null;
  /** e.g. 'climbing' | 'falling' | 'stable'. */
  trend: string | null;
}

export interface LeagueTierInfo {
  tierId: string;
  tierName: string;
  tierIndex: number;
  totalTiers: number;
  playerCount: number;
}

export interface LeagueInfo {
  leagueId: string;
  leagueName: string;
  /** 'CREATED' | 'ACTIVE' | 'ENDED' | … */
  leagueStatus: string;
  rankingMode?: string;
}

export interface LeagueEnrollment {
  league: LeagueInfo;
  tier: LeagueTierInfo;
  /** Rank within the tier. */
  rank: number;
  /** The player's score (e.g. CR Elo). */
  score: number;
  leagueRank?: number | null;
  momentum?: LeagueMomentum | null;
  isBanned?: boolean;
}

export interface MyLeaguesResponse {
  leagues: LeagueEnrollment[];
}

/** One row of a tier/league ranking. Carries only `sub` — no name (resolve via QSocials). */
export interface LeaguePlayerEntry {
  sub: string;
  rank: number;
  score: number;
  isBanned?: boolean;
  tierName?: string | null;
  tierIndex?: number | null;
}

export interface LeaguePagination {
  total: number;
  limit: number;
  offset: number;
  count: number;
  page: number;
  totalPages: number;
}

/** `GET /leagues/{id}/tiers/{tierId}/ranking` (standard mode). */
export interface TierRankingResponse {
  league: LeagueInfo;
  tier: LeagueTierInfo;
  players: LeaguePlayerEntry[];
  myRank?: number | null;
  myPage?: number | null;
  pagination?: LeaguePagination;
}

/**
 * `GET /leagues/{id}/ranking?sub=…&radius=…` — contextual ("±N around me") mode.
 *
 * Shape verified against QBank's code, NOT its design doc — the two disagree.
 * Notably `tier` is ALWAYS null here (this endpoint is cross-tier), and unlike
 * the player-card endpoint the `rivals` key is always present (possibly []).
 * `scorePerMovement`/`scoreVelocity` exist ONLY in contextual responses; they
 * are absent from the standard ranking rows despite what the doc shows.
 */
export interface LeagueRival {
  sub: string;
  rank: number | null;
  score: number;
  /** Score gap to the centre player. */
  deltaScore: number;
  /** Negative = above me, positive = below me. The centre itself is excluded. */
  position: number;
  isLateJoiner?: boolean;
}

export interface LeagueCentre {
  sub: string;
  rank: number | null;
  score: number;
  percentileTier?: number | null;
  percentileLeague?: number | null;
  leagueRank?: number | null;
  distanceToRank1?: number | null;
  distanceToMedian?: number | null;
  aboveMedian?: boolean | null;
  isBanned?: boolean;
}

export interface LeagueContextualRanking {
  league: LeagueInfo;
  /** Always null: this endpoint spans every tier. */
  tier: null;
  center: LeagueCentre;
  rivals: LeagueRival[];
}
